import { useEffect, useRef, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ProgressBar } from '@/components/ui/ProgressBar';
import FormFooter from '@/components/layout/FormFooter';
import { Check } from 'lucide-react';

interface FormLayoutProps {
  currentStep: number;
  completedSteps: number[];
  children: ReactNode;
  onBack: () => void;
  onNext: () => void;
  onStepClick?: (step: number) => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  lastSaved: Date | null;
  isSaving: boolean;
}

export default function FormLayout({
  currentStep,
  completedSteps,
  children,
  onBack,
  onNext,
  onStepClick,
  isFirstStep,
  isLastStep,
  lastSaved,
  isSaving,
}: FormLayoutProps) {
  const prevStepRef = useRef(currentStep);
  const direction = currentStep >= prevStepRef.current ? 1 : -1;
  prevStepRef.current = currentStep;
  const prefersReducedMotion = useReducedMotion();
  const slideX = prefersReducedMotion ? 0 : 60;

  // On step change, scroll the new card into view so mobile users start at the top.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const behavior: ScrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';
    window.scrollTo({ top: 0, behavior });
  }, [currentStep, prefersReducedMotion]);

  return (
    <div className="min-h-dvh flex flex-col py-4 sm:py-8" style={{ backgroundColor: 'var(--bg-page)' }}>
      {/* Sticky Progress Bar */}
      <ProgressBar currentStep={currentStep} completedSteps={completedSteps} onStepClick={onStepClick} />

      <div className="max-w-form mx-auto w-full flex-1 flex flex-col">
        {/* Draft Saved Indicator, attached to the step card above */}
        <div className="mt-2 px-4 sm:px-1 h-4 flex items-center justify-end gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
          {isSaving ? (
            <span>Saving...</span>
          ) : lastSaved ? (
            <>
              <Check size={12} style={{ color: 'var(--color-primary)' }} />
              <span>Saved</span>
            </>
          ) : null}
        </div>

        {/* Step Content with Directional Transition */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            initial={{ opacity: 0, x: direction * slideX }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -slideX }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-2 flex-1"
          >
            <div className="step-card">
              {children}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Form Footer */}
        <FormFooter
          onBack={onBack}
          onNext={onNext}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          isSubmitting={isSaving}
          showFullDisclaimer={isFirstStep || isLastStep}
        />
      </div>
    </div>
  );
}
